import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, Minus } from "lucide-react";
import { useState } from "react";
import { MaskLines, Reveal } from "./Motion";
import { marquee, primaryCta } from "@/content/site";

export function Btn({ to, href, children, variant = "primary", className = "", track, testid, ...rest }) {
  const base = "group inline-flex min-h-[50px] items-center justify-between gap-4 px-6 text-[15px] font-semibold transition-[background-color,color,border-color] duration-300";
  const styles = {
    primary: "bg-ink text-ivory hover:bg-accent",
    light: "bg-ivory text-ink hover:bg-accent hover:text-white",
    outline: "border border-ink text-ink hover:bg-ink hover:text-ivory",
    outlineLight: "border border-ivory/50 text-ivory hover:border-ivory hover:bg-ivory hover:text-ink",
  };
  const inner = (
    <>
      <span>{children}</span>
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
    </>
  );
  const props = { className: `${base} ${styles[variant]} ${className}`, "data-track": track, "data-testid": testid, ...rest };
  if (href) return <a href={href} {...props}>{inner}</a>;
  return <Link to={to} {...props}>{inner}</Link>;
}

export function TextLink({ to, href, children, testid, className = "", ...rest }) {
  const cls = `group inline-flex items-center gap-2 text-[15px] font-semibold ${className}`;
  const inner = (<><span className="link-u">{children}</span><ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" /></>);
  if (href) return <a href={href} data-testid={testid} className={cls} {...rest}>{inner}</a>;
  return <Link to={to} data-testid={testid} className={cls} {...rest}>{inner}</Link>;
}

export function Label({ children, className = "" }) {
  return <p className={`label ${className}`}>{children}</p>;
}

export function SectionHead({ label, lines, as = "h2", size = "h-lg", className = "", children }) {
  return (
    <div className={className}>
      {label && <Reveal><Label className="mb-6">{label}</Label></Reveal>}
      <MaskLines as={as} lines={lines} className={`display ${size}`} />
      {children && <Reveal delay={0.12} className="mt-6 max-w-xl lead">{children}</Reveal>}
    </div>
  );
}

export function PageHero({ label, lines, intro, children, testid }) {
  return (
    <section className="wrap pt-32 pb-14 md:pt-44 md:pb-20" data-testid={testid}>
      <Reveal><Label className="mb-8">{label}</Label></Reveal>
      <MaskLines as="h1" onLoad lines={lines} className="display h-xl max-w-6xl" delay={0.05} />
      {intro && <Reveal delay={0.3} className="mt-8 max-w-2xl lead">{intro}</Reveal>}
      {children}
    </section>
  );
}

export function BrowserFrame({ src, alt, label, className = "", priority = false, testid }) {
  return (
    <figure className={`overflow-hidden border border-ink/15 bg-ink shadow-[0_40px_80px_-40px_rgba(20,23,21,.55)] ${className}`} data-testid={testid}>
      <div className="flex h-8 items-center gap-3 border-b border-white/10 bg-[#1E2220] px-3">
        <span className="flex gap-1.5">{[0, 1, 2].map((i) => <span key={i} className="h-2 w-2 rounded-full bg-white/25" />)}</span>
        {label && <span className="mx-auto truncate font-mono text-[12px] text-white/70">{label}</span>}
      </div>
      <img src={src} alt={alt} width="1440" height="900" loading={priority ? "eager" : "lazy"} fetchPriority={priority ? "high" : "auto"} className="block aspect-[16/10] w-full object-cover object-top" />
    </figure>
  );
}

export function PhoneFrame({ src, alt, className = "" }) {
  return (
    <figure className={`overflow-hidden rounded-[28px] border-[6px] border-ink bg-ink shadow-[0_40px_80px_-30px_rgba(20,23,21,.6)] ${className}`}>
      <img src={src} alt={alt} width="390" height="844" loading="lazy" className="block aspect-[390/844] w-full rounded-[22px] object-cover object-top" />
    </figure>
  );
}

export function Marquee({ items = marquee }) {
  const row = [...items, ...items];
  return (
    <div className="marquee overflow-hidden border-y border-ink/15 py-5 md:py-7">
      <div className="marquee-track flex w-max">
        {[0, 1].map((k) => (
          <div key={k} className="flex shrink-0 items-center">
            {row.map((t, i) => (
              <span key={`${k}-${i}`} className="flex items-center" aria-hidden={k > 0 || i >= items.length ? "true" : undefined}>
                <span className="display whitespace-nowrap px-7 text-[2rem] md:text-[3.4rem]">{t}</span>
                <span className="h-2 w-2 bg-accent" aria-hidden="true" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function CtaBlock({ title = ["Et si vos prochains clients", "vous trouvaient plus facilement ?"], text = "Parlons de votre activité, de votre visibilité actuelle et de ce qu'un site peut vous apporter." }) {
  return (
    <section className="on-dark bg-ink py-20 text-ivory md:py-32" data-testid="cta-block">
      <div className="wrap">
        <Label className="mb-8">Prochaine étape</Label>
        <MaskLines lines={title} className="display h-xl max-w-5xl" />
        <Reveal delay={0.15} className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <p className="lead max-w-md">{text}</p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Btn to={primaryCta.to} variant="light" track="cta_diagnostic" testid="cta-block-diagnostic">Demander mon diagnostic</Btn>
            <Btn to="/contact" variant="outlineLight" track="cta_contact" testid="cta-block-contact">Parler de mon projet</Btn>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function FaqList({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="border-t border-ink/20" data-testid="faq-list">
      {items.map((f, i) => {
        const isOpen = open === i;
        return (
          <div key={f.q} className="border-b border-ink/20">
            <h3>
              <button type="button" aria-expanded={isOpen} aria-controls={`faq-${i}`} data-testid={`faq-toggle-${i}`} onClick={() => setOpen(isOpen ? -1 : i)} className="flex w-full items-center justify-between gap-6 py-5 text-left">
                <span className="display text-[1.4rem] leading-tight md:text-[1.75rem]">{f.q}</span>
                {isOpen ? <Minus className="h-5 w-5 shrink-0" /> : <Plus className="h-5 w-5 shrink-0" />}
              </button>
            </h3>
            <div id={`faq-${i}`} hidden={!isOpen} className="max-w-2xl pb-6 leading-relaxed text-mute">{f.a}</div>
          </div>
        );
      })}
    </div>
  );
}
